import { get, post } from '@/services/http';
import { GetUnapprovedPOsParams, UnapprovedPOsResponse } from '@/models/unapprovedPO';
import { EventHandler } from '@/modules/events';
import { StoreModel } from '@/models/store';

// US-037: Added approvalType for approval options
type ApprovalType = 'approve_only' | 'approve_and_confirm';

interface BulkApprovePayload {
  ids: number[];
  notes?: string;
  approvalType?: ApprovalType;
}

interface BulkRejectPayload {
  ids: number[];
  reason: string;
}

// Helper function to get event handler instance
function getEventHandler(): EventHandler {
  return new EventHandler({ store: new StoreModel() });
}

const purchaseOrderApprovalService = {
  /**
   * Get a list of unapproved purchase orders.
   * @param {GetUnapprovedPOsParams} params - Query parameters for filtering, pagination, etc.
   * @returns {Promise<any>}
   */
  getUnapprovedPOs(params: GetUnapprovedPOsParams): Promise<any> {
    return get('/purchase-orders/unapproved', params);
  },

  /**
   * Approve a single purchase order.
   * @param {number} id - The ID of the purchase order.
   * @param {object} data - Optional approval data including approvalType
   * @returns {Promise<any>}
   */
  async approveSingle(id: number, data?: { notes?: string; approvalType?: ApprovalType }): Promise<any> {
    const response = await post(`/purchase-orders/${id}/approve`, {
      notes: data?.notes,
      approvalType: data?.approvalType || 'approve_and_confirm',
    });

    // Track approval event
    const eventHandler = getEventHandler();
    eventHandler.trackAutomatedPurchaseOrderStep('PO Approved', {
      po_id: id,
      has_notes: !!(data?.notes),
      approval_type: data?.approvalType || 'approve_and_confirm'
    });

    return response;
  },

  /**
   * Reject a single purchase order.
   * @param {number} id - The ID of the purchase order.
   * @param {string} reason - The reason for rejection.
   * @returns {Promise<any>}
   */
  async rejectSingle(id: number, reason: string): Promise<any> {
    const response = await post(`/purchase-orders/${id}/reject`, { reason });
    
    // Track rejection event
    const eventHandler = getEventHandler();
    eventHandler.trackAutomatedPurchaseOrderStep('PO Rejected', {
      po_id: id,
      rejection_reason: reason
    });
    
    return response;
  },

  /**
   * Bulk approve purchase orders.
   * @param {BulkApprovePayload} payload - The payload containing the IDs and optional notes.
   * @returns {Promise<any>}
   */
  async bulkApprove(payload: BulkApprovePayload): Promise<any> {
    const response = await post('/purchase-orders/bulk-approve', {
      purchaseOrderIds: payload.ids,
      notes: payload.notes,
      approvalType: payload.approvalType || 'approve_and_confirm',
    });

    // Track bulk approval events
    const eventHandler = getEventHandler();
    payload.ids.forEach(id => {
      eventHandler.trackAutomatedPurchaseOrderStep('PO Approved', {
        po_id: id,
        has_notes: !!payload.notes,
        is_bulk_action: true,
        bulk_count: payload.ids.length,
        approval_type: payload.approvalType || 'approve_and_confirm'
      });
    });

    return response;
  },

  /**
   * Bulk reject purchase orders.
   * @param {BulkRejectPayload} payload - The payload containing the IDs and reason.
   * @returns {Promise<any>}
   */
  async bulkReject(payload: BulkRejectPayload): Promise<any> {
    const response = await post('/purchase-orders/bulk-reject', {
      purchaseOrderIds: payload.ids,
      reason: payload.reason,
    });
    
    // Track bulk rejection events
    const eventHandler = getEventHandler();
    payload.ids.forEach(id => {
      eventHandler.trackAutomatedPurchaseOrderStep('PO Rejected', {
        po_id: id,
        rejection_reason: payload.reason,
        is_bulk_action: true,
        bulk_count: payload.ids.length
      });
    });
    
    return response;
  },
};

export default purchaseOrderApprovalService;
